import { useEffect, useRef, useCallback } from 'react';

const DEFAULT_BUCKET_COUNT = 200;
const SHORT_VIDEO_THRESHOLD = 100; // seconds
const SYNC_INTERVAL_MS = 20 * 60 * 1000; // 20 minutes
const HEATMAP_ENDPOINT = 'heatmap';
const ERROR_ENDPOINT = '/logging/client-error';

function logSyncError(message: string): void {
  try {
    fetch(ERROR_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: 'data=' + encodeURIComponent(JSON.stringify({ message })),
      credentials: 'same-origin',
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}

/**
 * Tracks video playback position and periodically syncs bucket data to the
 * server for heatmap generation.  Replicates the bucket algorithm from the
 * legacy Heatmap.js.
 *
 * Pass `null` for `mediaEl` to disable tracking (e.g. when `trackHeatmap`
 * prop is false).
 */
export function useHeatmapTracker(
  mediaEl: HTMLMediaElement | null,
  fileId: number | undefined,
  linkId?: string,
): void {
  // --- Mutable state stored in refs to avoid re-renders ---
  const bucketsRef = useRef<number[]>([]);
  const bucketCountRef = useRef(DEFAULT_BUCKET_COUNT);
  const lastBucketRef = useRef(-1);
  const durationRef = useRef(0);
  const syncTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Keep fileId / linkId in refs so the beforeunload closure always sees
  // the current values without requiring effect re-runs.
  const fileIdRef = useRef(fileId);
  const linkIdRef = useRef(linkId);
  useEffect(() => { fileIdRef.current = fileId; }, [fileId]);
  useEffect(() => { linkIdRef.current = linkId; }, [linkId]);

  // --- Build URLSearchParams payload from non-zero buckets ---
  const buildSyncPayload = useCallback((): URLSearchParams | null => {
    const buckets = bucketsRef.current;
    const changes: [number, number][] = [];

    for (let i = 0; i < buckets.length; i++) {
      if (buckets[i] > 0) {
        changes.push([i, buckets[i]]);
      }
    }

    if (changes.length === 0) return null;

    const params = new URLSearchParams();
    params.set('changes', JSON.stringify(changes));
    params.set('file', String(fileIdRef.current));
    params.set('bucket_count', String(bucketCountRef.current));
    if (linkIdRef.current) {
      params.set('link', linkIdRef.current);
    }

    return params;
  }, []);

  // --- Zero out buckets after a successful sync ---
  const resetBuckets = useCallback(() => {
    bucketsRef.current = new Array(bucketCountRef.current).fill(0);
    lastBucketRef.current = -1;
  }, []);

  // --- Normal sync via fetch ---
  const syncViaFetch = useCallback(() => {
    const payload = buildSyncPayload();
    if (!payload) return;

    fetch(HEATMAP_ENDPOINT, {
      method: 'POST',
      body: payload,
      credentials: 'same-origin',
    })
      .then((res) => {
        if (res.ok) {
          resetBuckets();
        } else {
          logSyncError(`Heatmap sync failed: HTTP ${res.status}`);
        }
      })
      .catch((err: Error) => {
        logSyncError(`Heatmap sync error: ${err.message}`);
      });
  }, [buildSyncPayload, resetBuckets]);

  // --- Sync via sendBeacon (for page unload) ---
  const syncViaBeacon = useCallback(() => {
    const payload = buildSyncPayload();
    if (!payload) return;

    navigator.sendBeacon(HEATMAP_ENDPOINT, payload);
    // Optimistic reset — the page is closing anyway
    resetBuckets();
  }, [buildSyncPayload, resetBuckets]);

  // --- Main effect: attach listeners, set up sync timer ---
  useEffect(() => {
    if (!mediaEl || fileId == null) return;

    // Initialize buckets
    bucketsRef.current = new Array(bucketCountRef.current).fill(0);
    lastBucketRef.current = -1;

    const onLoadedMetadata = () => {
      const dur = mediaEl.duration;
      if (!dur || !isFinite(dur)) return;

      durationRef.current = dur;

      // Fewer buckets for short videos (matches legacy Heatmap.js)
      const totalSecs = Math.ceil(dur);
      if (totalSecs < SHORT_VIDEO_THRESHOLD) {
        bucketCountRef.current = totalSecs * 2;
      } else {
        bucketCountRef.current = DEFAULT_BUCKET_COUNT;
      }

      // Re-initialize buckets array to the new size
      bucketsRef.current = new Array(bucketCountRef.current).fill(0);
      lastBucketRef.current = -1;
    };

    const onTimeUpdate = () => {
      const duration = durationRef.current;
      if (duration <= 0) return;

      const currentTime = mediaEl.currentTime;
      if (isNaN(currentTime)) return;

      const bucketCount = bucketCountRef.current;
      const bucketIndex = Math.round(
        (currentTime / duration) * (bucketCount - 1),
      );

      if (bucketIndex !== lastBucketRef.current) {
        if (bucketIndex >= 0 && bucketIndex < bucketCount) {
          bucketsRef.current[bucketIndex]++;
        }
        lastBucketRef.current = bucketIndex;
      }
    };

    const onPause = () => syncViaFetch();
    const onBeforeUnload = () => syncViaBeacon();

    // Attach listeners
    mediaEl.addEventListener('loadedmetadata', onLoadedMetadata);
    mediaEl.addEventListener('timeupdate', onTimeUpdate);
    mediaEl.addEventListener('pause', onPause);
    window.addEventListener('beforeunload', onBeforeUnload);

    // If metadata already loaded, fire immediately
    if (mediaEl.readyState >= 1 && mediaEl.duration > 0) {
      onLoadedMetadata();
    }

    // Periodic 20-minute sync
    syncTimerRef.current = setInterval(syncViaFetch, SYNC_INTERVAL_MS);

    return () => {
      mediaEl.removeEventListener('loadedmetadata', onLoadedMetadata);
      mediaEl.removeEventListener('timeupdate', onTimeUpdate);
      mediaEl.removeEventListener('pause', onPause);
      window.removeEventListener('beforeunload', onBeforeUnload);

      if (syncTimerRef.current) {
        clearInterval(syncTimerRef.current);
        syncTimerRef.current = null;
      }

      // Final sync on unmount (covers SPA navigation while page is alive)
      syncViaFetch();
    };
  }, [mediaEl, fileId, syncViaFetch, syncViaBeacon]);
}
